import {
  Component,
  DestroyRef,
  effect,
  inject,
  ChangeDetectionStrategy,
  input,
  InputSignal,
} from '@angular/core';
import {
  UntypedFormControl,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  Platform,
  IonRow,
  IonCol,
  IonSkeletonText,
} from '@ionic/angular/standalone';
import { AlfredClientService } from '../../../../alfred-client.service';
import { SelfCheckFeedback } from '../../../../models/feedback/self-check-feedback.model';
import { AnswersSelfExam } from '../../../../models/on-interaction/answers-self-exam.model';
import { SeenFeedback } from '../../../../models/on-interaction/seen-feedback.model';
import { QuestionStep } from '../../../../models/step/question-step.model';
import {
  ImageView,
  ImageViewerComponent,
} from '../../../../shared/image-viewer/image-viewer.component';
import { StepSupplierService } from '../../../step-supplier.service';
import { QuestionAnswer } from '../../question-control/question-type.interface';
import { CloseButtonComponent } from '../../../../shared/close-button/close-button.component';
import { CounterComponent } from '../../../../shared/counter/counter.component';
import { QuestionControl } from '../../question-control/question-control.component';
import { ClueComponent } from '../../../../shared/clue/clue.component';
import { FooterComponent } from '../../../footer/footer.component';
import { FeedbackComponent } from '../../feedback/feedback.component';
import { SupportMailtoAlfredComponent } from '../../../../shared/support-mailto-alfred/support-mailto-alfred.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lib-image-beneath-question',
  templateUrl: './image-beneath-question.component.html',
  styleUrls: ['./image-beneath-question.component.scss'],
  imports: [
    IonRow,
    IonCol,
    CloseButtonComponent,
    CounterComponent,
    QuestionControl,
    ReactiveFormsModule,
    ClueComponent,
    ImageViewerComponent,
    FooterComponent,
    FeedbackComponent,
    SupportMailtoAlfredComponent,
    IonSkeletonText,
  ],
})
export class ImageBeneathQuestionComponent {
  public alfredClientService: AlfredClientService = inject(AlfredClientService);
  public platform: Platform = inject(Platform);
  public stepSupplierService: StepSupplierService = inject(StepSupplierService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  /**
   * HTML template injection of {@see ImageView}.
   */
  public readonly ImageView: typeof ImageView = ImageView;

  /**
   * The question that is currently being displayed in the template.
   */
  public readonly isLoading: InputSignal<boolean> = input(false);

  /**
   * The question step that should be displayed in the template.
   */
  public readonly step: InputSignal<QuestionStep | undefined> = input<
    QuestionStep | undefined
  >();

  constructor() {
    effect(() => {
      const step: QuestionStep | undefined = this.step();

      if (step) {
        this.handleFeedbackSubscription(step);

        // Reset the form when the passed question is a different question.
        // When the same question is passed this might contain feedback about it,
        // so the existing answer should still be shown.
        if (
          this.currentStep &&
          this.currentStep.question.id !== step.question.id
        ) {
          this._questionFormCtrl.reset();
        }

        this.currentStep = step;
      }
    });
  }

  /**
   * Controller holding the given answer.
   */
  public _questionFormCtrl: UntypedFormControl = new UntypedFormControl(
    null,
    Validators.required,
  );

  /**
   * The question that is currently being displayed in the template.
   */
  private currentStep!: QuestionStep;

  private feedbackSubscription: Subscription | undefined = undefined;

  /**
   * Method triggered when the previous button is clicked.
   */
  public onPrevClicked(): void {
    let answer: QuestionAnswer | undefined;
    if (this._questionFormCtrl.valid) {
      answer = this._questionFormCtrl.value;
    }

    this.stepSupplierService.onPrevClicked(this.step(), answer);
  }

  /**
   * Method triggered when the next button is clicked.
   */
  public onNextClicked(event: MouseEvent | KeyboardEvent): void {
    // disable hotkeys in directHandle mode
    const disableHotkeys: boolean =
      event instanceof KeyboardEvent &&
      this.alfredClientService.isDirectHandleMode();
    if (disableHotkeys || this.step()?.feedback) {
      return;
    }

    if (this._questionFormCtrl.invalid || this._questionFormCtrl.pristine) {
      return;
    }

    this.stepSupplierService.onNextAnswer(
      this.step(),
      this._questionFormCtrl.value,
    );
  }

  /**
   * Method triggered when the next question button in feedback component
   * is clicked.
   */
  public onNextFeedbackClick(event: AnswersSelfExam | SeenFeedback): void {
    const step: QuestionStep | undefined = this.step();
    if (step) {
      this.stepSupplierService.onNextClick(step, event);
    }
  }

  /**
   * Whether or not the feedback (if any) is of the type self check.
   */
  public isSelfCheckFeedback(): boolean {
    return this.step()?.feedback instanceof SelfCheckFeedback;
  }

  private handleFeedbackSubscription(step: QuestionStep): void {
    if (this.feedbackSubscription) {
      this.feedbackSubscription.unsubscribe();
    }
    this.feedbackSubscription = step.feedback$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((feedback) => {
        if (feedback) {
          this._questionFormCtrl.disable();
        } else {
          this._questionFormCtrl.enable();
        }
      });
  }
}
